import React from 'react';
import { Tooltip } from 'antd';
import { FL140 } from '../../../services/l204Api';
import { Mode } from '../hooks/useL204Mode';
import { isApprovalLocked } from '../helpers';

/** Permission props from useModulePermission() */
interface L204Permissions {
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canExport: boolean;
  canPrint: boolean;
  canApprove: boolean;
  hasAnyPermission: boolean;
}

interface ActionBarProps {
  mode: Mode;
  isEditing: boolean;
  record: FL140 | null;
  currentIdx: number;
  allKeysLength: number;
  permissions: L204Permissions;
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  onNavTo: (idx: number) => void;
  onExit: () => void;
}

/** PermBtn: wraps button with permission check + Tooltip */
const PermBtn: React.FC<{
  allowed: boolean;
  disabled: boolean;
  className?: string;
  title?: string;
  onClick?: () => void;
  children: React.ReactNode;
}> = ({ allowed, disabled, className, title, onClick, children }) => {
  const btn = (
    <button
      className={className || 'btn'}
      disabled={disabled || !allowed}
      title={title}
      onClick={onClick}
    >
      {children}
    </button>
  );
  if (!allowed) {
    return <Tooltip title="權限不足">{btn}</Tooltip>;
  }
  return btn;
};

const ActionBar: React.FC<ActionBarProps> = ({
  mode, isEditing, record,
  currentIdx, allKeysLength,
  permissions,
  onAdd, onModify, onDelete, onNavTo, onExit,
}) => {
  const { canView, canCreate, canEdit, canDelete, canPrint } = permissions;
  const canSave = mode === 'add' ? canCreate : canEdit;

  // BR-F01: approval lock — disable edit/delete if parent doc is approved
  const parentStatus = record?.parent_doc?.fl040_80;
  const locked = isApprovalLocked(parentStatus);

  return (
    <div className="action-bar">

      {/* Navigation */}
      <div className="action-group">
        <PermBtn allowed={canView} disabled={isEditing || currentIdx <= 0} onClick={() => onNavTo(0)}>最前</PermBtn>
        <PermBtn allowed={canView} disabled={isEditing || currentIdx <= 0} onClick={() => onNavTo(currentIdx - 1)}>上一筆</PermBtn>
        <PermBtn allowed={canView} disabled={isEditing || currentIdx >= allKeysLength - 1} onClick={() => onNavTo(currentIdx + 1)}>下一筆</PermBtn>
        <PermBtn allowed={canView} disabled={isEditing || currentIdx >= allKeysLength - 1} onClick={() => onNavTo(allKeysLength - 1)}>最後</PermBtn>
      </div>

      {/* CRUD */}
      <div className="action-group">
        {/* CMDADD: 新增/存檔 */}
        <PermBtn
          allowed={isEditing ? canSave : canCreate}
          disabled={false}
          className="btn btn-primary"
          onClick={onAdd}
        >
          {isEditing ? '存檔' : '新增'}
        </PermBtn>
        {/* CMDMODI: 修改/還原 — locked if parent approved */}
        <PermBtn
          allowed={isEditing ? true : canEdit}
          disabled={(!isEditing && !record) || (!isEditing && locked)}
          onClick={onModify}
        >
          {isEditing ? '還原' : '修改'}
        </PermBtn>
        {/* CMDDEL — locked if parent approved */}
        <PermBtn
          allowed={canDelete}
          disabled={isEditing || !record || locked}
          className="btn btn-danger"
          onClick={onDelete}
        >
          刪除
        </PermBtn>

        <div className="action-separator" />

        {/* 列印 */}
        <PermBtn allowed={canPrint} disabled={isEditing || !record}>列印</PermBtn>

        {/* 離開 */}
        <button className="btn" onClick={onExit}>離開</button>
      </div>
    </div>
  );
};

export default React.memo(ActionBar);
